import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  DollarSign,
  Loader2,
  CheckCircle,
  AlertCircle,
  CreditCard,
  QrCode,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdhocPaymentPage({ user }) {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // null = selection, "card" or "paynow"
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/payments/adhoc-booking/${bookingId}`,
        { withCredentials: true }
      );
      setBookingData(response.data);
      
      // Check if already paid
      if (response.data.booking?.payment_status === "paid") {
        toast.success("This booking has already been paid!");
        navigate("/academy/parent-dashboard");
      }
    } catch (err) {
      console.error("Error fetching booking:", err);
      setError(err.response?.data?.detail || "Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (method) => {
    try {
      setProcessingPayment(true);
      setPaymentMethod(method);
      
      const response = await axios.post(
        `${API}/payments/checkout/adhoc-booking`,
        {
          booking_id: bookingId,
          origin_url: window.location.origin,
          payment_method: method
        },
        { withCredentials: true }
      );
      
      if (method === "card") {
        // Stripe - redirect to checkout
        if (response.data.url) {
          toast.success(`Payment Reference: ${response.data.payment_reference}. Redirecting to payment...`);
          window.location.href = response.data.url;
        } else {
          throw new Error("Failed to create checkout session");
        }
      } else {
        // PayNow - redirect to PayNow page with params
        const { transaction_id, payment_reference, amount } = response.data;
        toast.success(`Payment Reference: ${payment_reference}. Redirecting to PayNow...`);
        navigate(`/academy/paynow-payment?ref=${payment_reference}&amount=${amount}&txn=${transaction_id}`);
      }
    } catch (err) {
      console.error("Error initiating payment:", err);
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setPaymentMethod(null);
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-slate-400 mb-4">{error}</p>
            <Link to="/academy/session-upgrade">
              <Button>Back to Session Upgrade</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { booking, session, player } = bookingData || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-900 p-4">
      <div className="max-w-lg mx-auto">
        <Link to="/academy/session-upgrade" className="inline-flex items-center text-slate-400 mb-4 hover:text-white">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Session Upgrade
        </Link>
        
        <Card>
          <CardHeader className="bg-slate-950 border-b border-white/5 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Complete Payment
            </CardTitle>
            <p className="text-blue-100 text-sm">
              {booking?.booking_type === "term_upgrade" ? "Mid-term Session Upgrade" : "Single Session Booking"}
            </p>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Booking Summary */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-[#0a0f1a] rounded-lg">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-400">Player</p>
                    <p className="font-medium">{player?.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#0a0f1a] rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-400">Session</p>
                    {booking?.line_items?.length > 0 ? (
                      <div className="space-y-1 mt-1" data-testid="adhoc-payment-line-items">
                        {booking.line_items.map((li, idx) => {
                          const dayNames = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                          return (
                            <div key={idx} className="flex items-center justify-between gap-3">
                              <p className="text-sm font-medium">
                                {dayNames[li.day_of_week]}{' '}
                                {new Date(li.specific_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                {li.start_time ? ` · ${li.start_time}` : ''}
                              </p>
                              <span className="text-sm text-slate-300">${li.amount?.toFixed(2)}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <>
                        <p className="font-medium">{session?.name || "Training Session"}</p>
                        <p className="text-sm text-slate-400">
                          {session?.day_of_week}s, {session?.start_time} - {session?.end_time}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {session?.location && (
                <div className="flex items-center gap-3 p-3 bg-[#0a0f1a] rounded-lg">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-400">Location</p>
                    <p className="font-medium">{session.location}</p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 p-3 bg-[#0a0f1a] rounded-lg">
                <span className="text-sm text-slate-400">Reference</span>
                <Badge variant="outline" className="font-mono">
                  {booking?.booking_reference}
                </Badge>
              </div>
            </div>
            
            {/* Amount */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <span className="text-lg font-medium">Total Amount</span>
                <span className="text-2xl font-bold text-blue-400">
                  ${booking?.total_amount?.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-300">Select Payment Method</h3>
              
              {/* Card Payment Option */}
              <Button
                onClick={() => handlePayment("card")}
                disabled={processingPayment}
                className="w-full h-auto py-4 bg-slate-950 border-b border-white/5 hover:from-blue-700 hover:to-indigo-700"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-6 h-6" />
                    <div className="text-left">
                      <p className="font-semibold">Credit / Debit Card</p>
                      <p className="text-xs text-blue-200">Visa, Mastercard, AMEX via Stripe</p>
                    </div>
                  </div>
                  {processingPayment && paymentMethod === "card" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  )}
                </div>
              </Button>
              
              {/* PayNow Option */}
              <Button
                onClick={() => handlePayment("paynow")}
                disabled={processingPayment}
                variant="outline"
                className="w-full h-auto py-4 border-2 border-green-500 hover:bg-emerald-500/10"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <QrCode className="w-6 h-6 text-emerald-400" />
                    <div className="text-left">
                      <p className="font-semibold text-emerald-400">PayNow (QR Code)</p>
                      <p className="text-xs text-emerald-400">Scan with DBS, OCBC, UOB apps</p>
                    </div>
                  </div>
                  {processingPayment && paymentMethod === "paynow" ? (
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                  ) : (
                    <ArrowLeft className="w-5 h-5 rotate-180 text-emerald-400" />
                  )}
                </div>
              </Button>
            </div>
            
            <p className="text-xs text-center text-slate-400">
              Your session will be added once payment is verified.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
